"use client";

import { track as vercelTrack } from "@vercel/analytics";
import { useConsentStore } from "@/lib/analytics/consent";
import { trackCoreAction, trackPostHog } from "@/lib/services/posthog";

// Declare global dataLayer for GTM
declare global {
	interface Window {
		dataLayer: Record<string, unknown>[];
	}
}

/**
 * Push events to Google Tag Manager dataLayer
 * This allows GTM to handle all pixel tracking
 */
export function pushToDataLayer(event: string, parameters?: Record<string, unknown>) {
	if (typeof window !== "undefined") {
		window.dataLayer = window.dataLayer || [];
		window.dataLayer.push({
			event: event,
			...parameters
		});
	}
}

// Type for Vercel Analytics properties - matches Vercel's AllowedPropertyValues
type AnalyticsProperties = Record<string, string | number | boolean | null>;

// Business-focused event types
export type BusinessEvent =
	| {
			name: "festival_viewed";
			properties: {
				festival_id: string;
				festival_name?: string;
				source: "discover" | "search" | "profile" | "direct" | "social";
			};
	  }
	| {
			name: "festival_lineup_explored";
			properties: {
				festival_id: string;
				festival_name?: string;
				tab: "lineup" | "schedule" | "calendar" | "time" | "stages" | "reviews";
			};
	  }
	| {
			name: "festival_schedule_built";
			properties: {
				festival_id: string;
				festival_name?: string;
				sets_added: number;
			};
	  }
	| {
			name: "set_interaction";
			properties: {
				festival_id: string;
				set_id: string;
				artist_name?: string;
				action:
					| "reviewed"
					| "want_to_see"
					| "must_see"
					| "schedule_added"
					| "schedule_removed";
			};
	  }
	| {
			name: "review_submitted";
			properties: {
				festival_id: string;
				set_id?: string;
				type: "set" | "festival";
				rating: number;
				has_text: boolean;
			};
	  }
	| {
			name: "schedule_exported";
			properties: {
				festival_id: string;
				festival_name?: string;
				set_count: number;
				format: "wallpaper";
			};
	  }
	| {
			name: "show_viewed";
			properties: {
				show_id: string;
				show_title?: string;
				source: "discover" | "search" | "profile" | "direct" | "social";
			};
	  }
	| {
			name: "artist_discovery";
			properties: {
				artist_id: string;
				artist_name?: string;
				source: "festival" | "search" | "discover" | "profile";
				action: "viewed" | "followed";
			};
	  }
	| {
			name: "auth_signup_started";
			properties: {
				method: "email" | "google" | "apple";
			};
	  }
	| {
			name: "auth_signup_completed";
			properties: {
				method: "email" | "google" | "apple";
			};
	  }
	| {
			name: "auth_signin";
			properties: {
				method: "email" | "google" | "apple" | "magic_link";
			};
	  }
	| {
			name: "profile_setup_completed";
			properties: {
				fields_filled: number;
				has_avatar: boolean;
				is_private: boolean;
			};
	  }
	| {
			name: "app_install_initiated";
			properties: {
				source: "ios_smart_banner" | "android_banner";
			};
	  }
	| {
			name: "user_followed";
			properties: {
				followed_user_id: string;
				source: "profile" | "facepile" | "search";
			};
	  }
	| {
			name: "invitation_sent";
			properties: {
				festival_id: string;
				recipient_count: number;
			};
	  }
	| {
			name: "invitation_accepted";
			properties: {
				festival_id: string;
				from_user_id: string;
			};
	  }
	| {
			name: "profile_visited";
			properties: {
				profile_user_id: string;
				source: "search" | "facepile" | "link" | "invitation";
			};
	  }
	| {
			name: "global_search";
			properties: {
				query: string;
				tab: "all" | "festivals" | "artists" | "users";
			};
	  }
	| {
			name: "search_result_clicked";
			properties: {
				result_type: "festival" | "artist" | "user";
				position: number;
				query: string;
			};
	  }
	| {
			name: "festival_attendance_set";
			properties: {
				festival_id: string;
				festival_name: string;
				status: "going" | "interested" | "not_going";
				value: number;
			};
	  }
	| {
			name: "show_attendance_set";
			properties: {
				show_id: string;
				show_name: string;
				venue: string;
				status: "going" | "interested";
				value: number;
			};
	  }
	| {
			name: "show_review_submitted";
			properties: Record<string, string | number | boolean | undefined>;
	  }
	| {
			name: "set_review_submitted";
			properties: Record<string, string | number | boolean | undefined>;
	  };

/**
 * Track business events with dual analytics (Vercel + PostHog)
 * Only tracks in production to keep data clean
 * Respects user consent preferences
 */
/**
 * High-intent events that should be pushed to GTM dataLayer for pixel tracking
 * Maps internal event names to standard pixel conversion events
 */
const HIGH_INTENT_EVENT_MAPPING: Record<string, string> = {
	// Festival events
	'festival_attendance_set': 'AddToCart',     // User marks attendance interest
	'festival_review_submitted': 'Purchase',     // User submits festival review

	// Set/Show review events (all map to Purchase as high-value actions)
	'review_submitted': 'Purchase',              // Generic review (festival/set/show)
	'set_review_submitted': 'Purchase',          // Festival set review
	'show_review_submitted': 'Purchase',         // Show main artist review
	'show_set_review_submitted': 'Purchase',     // Show supporting act review

	// Show events
	'show_attendance_set': 'AddToCart',         // User marks show attendance

	// Other high-value actions
	'schedule_exported': 'Lead',                // User exports schedule
	'user_followed': 'Subscribe',               // Social engagement
	'invitation_sent': 'InitiateCheckout',      // Viral growth signal
	'invitation_accepted': 'CompleteRegistration' // Viral conversion
};

/**
 * Calculate event value based on event type for ROAS optimization
 */
function getEventValue(event: string, properties?: Record<string, unknown>): number | undefined {
	// Assign values based on event importance
	if (event.includes('review')) {
		// Reviews are highest value, scale by rating if available
		const rating = (properties?.rating as number) || 3;
		return rating * 2; // $2-10 value
	}
	if (event.includes('attendance') && properties?.status === 'going') {
		return 10; // Confirmed attendance is high value
	}
	if (event.includes('attendance') && properties?.status === 'interested') {
		return 5; // Interest is medium value
	}
	if (event === 'schedule_exported') {
		return 8; // High engagement signal
	}
	if (event === 'user_followed' || event === 'invitation_sent') {
		return 3; // Social signals
	}
	return undefined;
}

export function track<T extends BusinessEvent>(
	event: T["name"],
	properties?: T["properties"],
): void {
	// Only track in production
	if (process.env.NODE_ENV !== "production") {
		return;
	}

	const consentStore = useConsentStore.getState();

	// Track with Vercel Analytics if consent is given
	if (consentStore.canTrack()) {
		try {
			vercelTrack(event, properties as AnalyticsProperties);
		} catch (error) {
			// Fail silently to not impact user experience
			console.error("[Vercel Analytics Error]", error);
		}
	}

	// Track with PostHog if consent is given
	if (consentStore.canTrackPostHog()) {
		try {
			trackPostHog(event, properties as Record<string, unknown>);

			// Also track core actions for MAU/DAU metrics
			if (isCoreAction(event)) {
				trackCoreAction(event, properties as Record<string, unknown>);
			}

			// Push high-intent events to GTM dataLayer for pixel tracking
			if (HIGH_INTENT_EVENT_MAPPING[event] || event.includes('review') || event.includes('attendance')) {
				const pixelEvent = HIGH_INTENT_EVENT_MAPPING[event] || event;
				const eventValue = getEventValue(event, properties);

				pushToDataLayer(pixelEvent, {
					...properties,
					event_category: 'high_intent',
					event_label: event,
					value: eventValue,
					currency: eventValue ? 'USD' : undefined,
					content_type: (properties as Record<string, unknown>)?.type as string | undefined || event.split('_')[0]
				});
			}
		} catch (error) {
			// Fail silently to not impact user experience
			console.error("[PostHog Analytics Error]", error);
		}
	}
}

/**
 * Helper function to determine if an event is a core action for MAU/DAU tracking
 * These actions define what makes a user "active" for MAU/DAU calculation
 */
function isCoreAction(
	event: string,
): event is
	| "festival_viewed"
	| "set_interaction"
	| "festival_lineup_explored"
	| "schedule_exported"
	| "review_submitted"
	| "user_followed"
	| "global_search"
	| "search_result_clicked"
	| "artist_discovery" {
	const coreActions = [
		"festival_viewed", // Views a festival page
		"show_viewed", // Views a show page
		"set_interaction", // Interacts with their schedule (want/must see, etc.)
		"festival_lineup_explored", // Explores festival content (lineup, schedule, etc.)
		"schedule_exported", // Exports schedule (high engagement)
		"review_submitted", // Writes/edits a review
		"user_followed", // Engages with social features
		"global_search", // Searches for content
		"search_result_clicked", // Clicks search results
		"artist_discovery", // Discovers and views artist content
	];
	return coreActions.includes(event);
}

/**
 * Track MAU/DAU activity - call this for any action that indicates user engagement
 * This ensures consistent tracking across both Vercel and PostHog
 */
export function trackUserActivity(
	activity:
		| "festival_interaction"
		| "schedule_management"
		| "review_activity"
		| "social_engagement"
		| "content_discovery",
	properties?: Record<string, unknown>,
): void {
	// Only track in production
	if (process.env.NODE_ENV !== "production") {
		return;
	}

	const consentStore = useConsentStore.getState();

	// Track with PostHog for MAU/DAU metrics
	if (consentStore.canTrackPostHog()) {
		try {
			trackCoreAction(activity, properties);
		} catch (error) {
			console.error("[PostHog MAU/DAU] Error tracking user activity:", error);
		}
	}
}

/**
 * Track daily active user engagement
 * Call this once per day per user for any significant activity
 */
export function trackDailyActiveUser(
	properties?: Record<string, unknown>,
): void {
	// Only track in production
	if (process.env.NODE_ENV !== "production") {
		return;
	}

	const consentStore = useConsentStore.getState();

	// Track with PostHog for DAU metrics
	if (consentStore.canTrackPostHog()) {
		try {
			trackCoreAction("daily_active_user", {
				date: new Date().toISOString().split("T")[0], // YYYY-MM-DD format
				...properties,
			});
		} catch (error) {
			console.error("[PostHog DAU] Error tracking daily active user:", error);
		}
	}
}

/**
 * Track monthly active user engagement
 * Call this once per month per user for any significant activity
 */
export function trackMonthlyActiveUser(
	properties?: Record<string, unknown>,
): void {
	// Only track in production
	if (process.env.NODE_ENV !== "production") {
		return;
	}

	const consentStore = useConsentStore.getState();

	// Track with PostHog for MAU metrics
	if (consentStore.canTrackPostHog()) {
		try {
			trackCoreAction("monthly_active_user", {
				month: new Date().toISOString().substring(0, 7), // YYYY-MM format
				...properties,
			});
		} catch (error) {
			console.error("[PostHog MAU] Error tracking monthly active user:", error);
		}
	}
}

/**
 * Track festival engagement - use when user views a festival page
 */
export function trackFestivalView(
	festivalId: string,
	festivalName?: string,
	source: "discover" | "search" | "profile" | "direct" | "social" = "direct",
): void {
	track("festival_viewed", {
		festival_id: festivalId,
		festival_name: festivalName,
		source,
	});

	// Track user activity for MAU/DAU metrics
	trackUserActivity("festival_interaction", {
		festival_id: festivalId,
		festival_name: festivalName,
		source,
	});

	// Push to GTM dataLayer for pixel tracking
	if (festivalName) {
		pushToDataLayer("view_content", {
			content_type: "festival",
			content_id: festivalId,
			content_name: festivalName,
			content_category: "festival",
			value: undefined,
			currency: "USD",
		});
	}
}

/**
 * Track show engagement - use when user views a show page
 */
export function trackShow(
	showId: string,
	showTitle?: string,
	action: "view" = "view",
	source: "discover" | "search" | "profile" | "direct" | "social" = "direct",
): void {
	track("show_viewed", {
		show_id: showId,
		show_title: showTitle,
		source,
	});

	// Track user activity for MAU/DAU metrics
	trackUserActivity("content_discovery", {
		show_id: showId,
		show_title: showTitle,
		source,
		action,
	});

	// Push to GTM dataLayer for pixel tracking
	if (showTitle) {
		pushToDataLayer("view_content", {
			content_type: "show",
			content_id: showId,
			content_name: showTitle,
			content_category: "show",
			value: undefined,
			currency: "USD",
		});
	}
}

/**
 * Track show attendance status - HIGH INTENT EVENT
 * Use when user marks their attendance status for a show
 */
export function trackShowAttendance(
	showId: string,
	showName: string,
	venue: string,
	status: "going" | "interested",
): void {
	track("show_attendance_set", {
		show_id: showId,
		show_name: showName,
		venue,
		status,
		value: status === "going" ? 8 : 4,
	});

	// Track user activity for MAU/DAU metrics
	trackUserActivity("content_discovery", {
		show_id: showId,
		show_name: showName,
		venue,
		action: `attendance_${status}`,
	});
}

/**
 * Track festival tab interactions - indicates depth of exploration
 */
export function trackFestivalTabInteraction(
	festivalId: string,
	festivalName: string | undefined,
	tab: "lineup" | "schedule" | "calendar" | "time" | "stages" | "reviews",
): void {
	track("festival_lineup_explored", {
		festival_id: festivalId,
		festival_name: festivalName,
		tab,
	});
}

/**
 * Track festival attendance status - HIGH INTENT EVENT
 * Use when user marks their attendance status for a festival
 */
export function trackFestivalAttendance(
	festivalId: string,
	festivalName: string,
	status: "going" | "interested" | "not_going",
): void {
	track("festival_attendance_set", {
		festival_id: festivalId,
		festival_name: festivalName,
		status,
		value: status === "going" ? 10 : status === "interested" ? 5 : 0,
	});

	// Track user activity for MAU/DAU metrics
	trackUserActivity("festival_interaction", {
		festival_id: festivalId,
		festival_name: festivalName,
		action: `attendance_${status}`,
	});
}

/**
 * Track set interactions - core engagement metric
 */
export function trackSetInteraction(
	festivalId: string,
	setId: string,
	action:
		| "reviewed"
		| "want_to_see"
		| "must_see"
		| "schedule_added"
		| "schedule_removed",
	artistName?: string,
): void {
	track("set_interaction", {
		festival_id: festivalId,
		set_id: setId,
		artist_name: artistName,
		action,
	});

	// Track user activity for MAU/DAU metrics
	trackUserActivity("schedule_management", {
		festival_id: festivalId,
		set_id: setId,
		artist_name: artistName,
		action,
	});
}

/**
 * Track review submissions - HIGH INTENT EVENT
 * Supports both old and new signatures for backward compatibility
 */
export function trackReviewSubmission(
	idOrFestivalId: string,
	nameOrRating: string | number,
	ratingOrType: number | "set" | "festival" | "festival_set" | "show" | "show_set",
	typeOrSetId?: "festival" | "festival_set" | "show" | "show_set" | string,
	metadataOrHasText?: {
		festivalId?: string;
		festivalName?: string;
		showId?: string;
		venue?: string;
		artistType?: "main" | "supporting";
		hasText?: boolean;
	} | boolean,
): void {
	// Check if using old signature: trackReviewSubmission(festivalId, rating, type, setId?, hasText?)
	if (typeof nameOrRating === "number" && (ratingOrType === "set" || ratingOrType === "festival")) {
		// Old signature - convert to new format
		const festivalId = idOrFestivalId;
		const rating = nameOrRating;
		const type = ratingOrType as "set" | "festival";
		const setId = typeOrSetId as string | undefined;
		const hasText = metadataOrHasText as boolean | undefined;

		// Call with new signature format
		if (type === "festival") {
			trackReviewSubmissionNew(
				festivalId,
				"", // Festival name not available in old signature
				rating,
				"festival",
				{ hasText: hasText || false }
			);
		} else {
			trackReviewSubmissionNew(
				setId || "",
				"", // Artist name not available in old signature
				rating,
				"festival_set",
				{
					festivalId,
					hasText: hasText || false
				}
			);
		}
		return;
	}

	// New signature - pass through
	trackReviewSubmissionNew(
		idOrFestivalId,
		nameOrRating as string,
		ratingOrType as number,
		typeOrSetId as "festival" | "festival_set" | "show" | "show_set",
		metadataOrHasText as {
			festivalId?: string;
			festivalName?: string;
			showId?: string;
			venue?: string;
			artistType?: "main" | "supporting";
			hasText?: boolean;
		}
	);
}

/**
 * Internal implementation of review tracking
 */
function trackReviewSubmissionNew(
	id: string,
	name: string,
	rating: number,
	type: "festival" | "festival_set" | "show" | "show_set",
	metadata?: {
		festivalId?: string;
		festivalName?: string;
		showId?: string;
		venue?: string;
		artistType?: "main" | "supporting";
		hasText?: boolean;
	},
): void {
	// Determine the correct event name based on type
	const eventName = type === "show" || type === "show_set"
		? "show_review_submitted"
		: type === "festival_set"
			? "set_review_submitted"
			: "review_submitted";

	// Build properties based on type
	const properties: Record<string, string | number | boolean> = {
		type,
		rating,
		has_text: metadata?.hasText || false,
		value: rating * 2, // Higher ratings = higher value signal
	};

	// Add type-specific properties
	if (type === "festival") {
		properties.festival_id = id;
		properties.festival_name = name;
	} else if (type === "festival_set") {
		properties.set_id = id;
		properties.artist_name = name;
		if (metadata?.festivalId) properties.festival_id = metadata.festivalId;
		if (metadata?.festivalName) properties.festival_name = metadata.festivalName;
	} else if (type === "show") {
		properties.show_id = id;
		properties.show_name = name;
		if (metadata?.venue) properties.venue = metadata.venue;
		properties.artist_type = "main";
	} else if (type === "show_set") {
		properties.set_id = id;
		properties.artist_name = name;
		if (metadata?.showId) properties.show_id = metadata.showId;
		if (metadata?.venue) properties.venue = metadata.venue;
		properties.artist_type = metadata?.artistType || "supporting";
	}

	// Track the event - cast properties to match expected type
	if (eventName === "review_submitted") {
		track("review_submitted", {
			festival_id: (properties.festival_id as string) || "",
			set_id: properties.set_id as string | undefined,
			type: properties.type === "festival_set" ? "set" : "festival",
			rating: properties.rating as number,
			has_text: properties.has_text as boolean,
		});
	} else {
		// For other review types (show reviews), use generic track
		track(eventName as BusinessEvent["name"], properties as Record<string, string | number | boolean>);
	}

	// Track user activity for MAU/DAU metrics - cast to match expected type
	if (type === "festival" || type === "festival_set") {
		trackUserActivity("review_activity", {
			festival_id: properties.festival_id || "",
			set_id: properties.set_id,
			type: type === "festival" ? "festival" : "set",
			rating: properties.rating,
			has_text: properties.has_text,
		});
	} else {
		// For show reviews, track as content discovery instead
		trackUserActivity("content_discovery", {
			show_id: properties.show_id || id,
			action: "reviewed",
		});
	}
}

/**
 * Legacy helper for tracking show reviews - HIGH INTENT EVENT
 */
export function trackShowReview(
	showId: string,
	showName: string,
	rating: number,
	venue?: string,
	hasText = false,
	artistType: "main" | "supporting" = "main",
): void {
	trackReviewSubmission(
		showId,
		showName,
		rating,
		artistType === "main" ? "show" : "show_set",
		{
			venue,
			hasText,
			artistType,
		}
	);
}

/**
 * Track schedule exports - high engagement indicator
 */
export function trackScheduleExport(
	festivalId: string,
	festivalName: string | undefined,
	setCount: number,
): void {
	// Note: Using schedule_export_completed to match mobile and PostHog insights
	track("schedule_exported", {
		festival_id: festivalId,
		festival_name: festivalName,
		set_count: setCount,
		format: "wallpaper",
	});

	// Also track as schedule_export_completed for PostHog insights compatibility
	const consentStore = useConsentStore.getState();
	if (consentStore.canTrackPostHog()) {
		trackPostHog("schedule_export_completed", {
			festival_id: festivalId,
			festival_name: festivalName,
			set_count: setCount,
			format: "wallpaper",
		});
	}
}

/**
 * Track artist discovery and interactions
 */
export function trackArtistDiscovery(
	artistId: string,
	artistName: string | undefined,
	source: "festival" | "search" | "discover" | "profile",
	action: "viewed" | "followed",
): void {
	track("artist_discovery", {
		artist_id: artistId,
		artist_name: artistName,
		source,
		action,
	});

	// Push to GTM dataLayer for pixel tracking
	if (action === "viewed" && artistName) {
		pushToDataLayer("view_content", {
			content_type: "artist",
			content_id: artistId,
			content_name: artistName,
			content_category: "artist",
			value: undefined,
			currency: "USD",
		});
	}
}

/**
 * Track authentication events
 */
export function trackAuthEvent(
	event: "signup_started" | "signup_completed" | "signin",
	method: "email" | "google" | "apple" | "magic_link",
): void {
	const eventName =
		event === "signup_started"
			? "auth_signup_started"
			: event === "signup_completed"
				? "auth_signup_completed"
				: "auth_signin";

	track(eventName as BusinessEvent["name"], { method });

	// Push to GTM dataLayer for pixel tracking
	if (event === "signup_completed") {
		// Push custom event for GTM trigger configuration
		pushToDataLayer("signup_completed", {
			method: method,
			value: undefined,
			currency: "USD",
			// Include parameters for all pixels
			registration_method: method, // For Meta Pixel
			signup_method: method, // For Reddit Pixel
		});

		// ALSO push CompleteRegistration directly for Meta Pixel standard event
		// This ensures pixel fires even if GTM trigger isn't configured
		pushToDataLayer("CompleteRegistration", {
			method: method,
			registration_method: method,
			signup_method: method,
			value: undefined,
			currency: "USD",
		});
	}
}

/**
 * Track app install initiated - when user clicks app download banner
 */
export function trackAppInstallInitiated(
	source: "ios_smart_banner" | "android_banner",
): void {
	track("app_install_initiated", {
		source,
	});

	// Push to GTM dataLayer for pixel tracking
	pushToDataLayer("app_install_initiated", {
		source: source,
		platform: source === "ios_smart_banner" ? "ios" : "android",
	});
}

/**
 * Track profile setup completion
 */
export function trackProfileSetup(
	fieldsCount: number,
	hasAvatar: boolean,
	isPrivate: boolean,
): void {
	track("profile_setup_completed", {
		fields_filled: fieldsCount,
		has_avatar: hasAvatar,
		is_private: isPrivate,
	});
}

/**
 * Track social interactions - follows
 */
export function trackUserFollowed(
	followedUserId: string,
	source: "profile" | "facepile" | "search",
): void {
	track("user_followed", {
		followed_user_id: followedUserId,
		source,
	});

	// Track user activity for MAU/DAU metrics
	trackUserActivity("social_engagement", {
		followed_user_id: followedUserId,
		source,
	});
}

/**
 * Track social interactions - invitations
 */
export function trackInvitation(
	type: "sent" | "accepted",
	festivalId: string,
	data: { recipientCount?: number; fromUserId?: string },
): void {
	if (type === "sent" && data.recipientCount) {
		track("invitation_sent", {
			festival_id: festivalId,
			recipient_count: data.recipientCount,
		});

		// Track as viral action for growth metrics
		trackViralAction("invitation_sent", {
			festival_id: festivalId,
			recipient_count: data.recipientCount,
		});
	} else if (type === "accepted" && data.fromUserId) {
		track("invitation_accepted", {
			festival_id: festivalId,
			from_user_id: data.fromUserId,
		});

		// Track as viral action for growth metrics
		trackViralAction("invitation_accepted", {
			festival_id: festivalId,
			from_user_id: data.fromUserId,
		});
	}
}

/**
 * Track profile visits
 */
export function trackProfileVisit(
	profileUserId: string,
	source: "search" | "facepile" | "link" | "invitation",
): void {
	track("profile_visited", {
		profile_user_id: profileUserId,
		source,
	});
}

/**
 * Track group/collaboration discovery events
 */
export function trackGroupDiscovery(
	groupId: string,
	groupName: string,
	source: "festival" | "search" | "discover" | "profile",
	action: "viewed" | "clicked",
): void {
	track("artist_discovery", {
		artist_id: groupId,
		artist_name: groupName,
		source,
		action: action === "clicked" ? "viewed" : action,
	});
}

/**
 * Track search interactions
 */
export function trackSearch(
	query: string,
	tab: "all" | "festivals" | "artists" | "users" = "all",
): void {
	track("global_search", {
		query,
		tab,
	});

	// Track user activity for MAU/DAU metrics
	trackUserActivity("content_discovery", {
		query,
		tab,
	});
}

/**
 * Track search result clicks
 */
export function trackSearchResultClick(
	resultType: "festival" | "artist" | "user",
	position: number,
	query: string,
): void {
	track("search_result_clicked", {
		result_type: resultType,
		position,
		query,
	});

	// Track user activity for MAU/DAU metrics
	trackUserActivity("content_discovery", {
		result_type: resultType,
		position,
		query,
	});
}

// Growth-specific event tracking functions

/**
 * Track viral actions that can lead to growth
 */
export function trackViralAction(
	action:
		| "invitation_sent"
		| "invitation_accepted"
		| "social_share"
		| "referral_link_shared",
	properties?: Record<string, unknown>,
): void {
	// Only track in production
	if (process.env.NODE_ENV !== "production") {
		return;
	}

	const consentStore = useConsentStore.getState();

	// Track with both Vercel and PostHog
	if (consentStore.canTrack()) {
		try {
			vercelTrack(action, properties as AnalyticsProperties);
		} catch (error) {
			console.error("[Vercel Analytics] Error tracking viral action:", error);
		}
	}

	if (consentStore.canTrackPostHog()) {
		try {
			trackPostHog(action, properties);
			// Also track as a viral action for PostHog
			import("@/lib/services/posthog").then(({ trackViralAction }) => {
				trackViralAction(action, properties);
			});
		} catch (error) {
			console.error("[PostHog Analytics] Error tracking viral action:", error);
		}
	}
}

/**
 * Track user activation events
 */
export function trackUserActivation(
	event:
		| "first_schedule_created"
		| "first_review_written"
		| "first_friend_added",
	properties?: Record<string, unknown>,
): void {
	// Only track in production
	if (process.env.NODE_ENV !== "production") {
		return;
	}

	const consentStore = useConsentStore.getState();

	if (consentStore.canTrackPostHog()) {
		try {
			import("@/lib/services/posthog").then(({ trackUserActivation }) => {
				trackUserActivation(event, properties);
			});
		} catch (error) {
			console.error(
				"[PostHog Analytics] Error tracking user activation:",
				error,
			);
		}
	}
}

/**
 * Track user retention events
 */
export function trackUserRetention(
	daysSinceLastVisit: number,
	returnTrigger: "notification" | "email" | "organic" | "direct",
	properties?: Record<string, unknown>,
): void {
	// Only track in production
	if (process.env.NODE_ENV !== "production") {
		return;
	}

	const consentStore = useConsentStore.getState();

	if (consentStore.canTrackPostHog()) {
		try {
			import("@/lib/services/posthog").then(({ trackUserRetention }) => {
				trackUserRetention(daysSinceLastVisit, returnTrigger, properties);
			});
		} catch (error) {
			console.error(
				"[PostHog Analytics] Error tracking user retention:",
				error,
			);
		}
	}
}

/**
 * Track feature adoption
 */
export function trackFeatureAdoption(
	feature: string,
	action: "first_use" | "regular_use" | "advanced_use",
	properties?: Record<string, unknown>,
): void {
	// Only track in production
	if (process.env.NODE_ENV !== "production") {
		return;
	}

	const consentStore = useConsentStore.getState();

	if (consentStore.canTrackPostHog()) {
		try {
			import("@/lib/services/posthog").then(({ trackFeatureAdoption }) => {
				trackFeatureAdoption(feature, action, properties);
			});
		} catch (error) {
			console.error(
				"[PostHog Analytics] Error tracking feature adoption:",
				error,
			);
		}
	}
}

/**
 * Track content creation events
 */
export function trackContentCreation(
	contentType: "review" | "schedule" | "photo" | "playlist",
	properties?: Record<string, unknown>,
): void {
	// Only track in production
	if (process.env.NODE_ENV !== "production") {
		return;
	}

	const consentStore = useConsentStore.getState();

	if (consentStore.canTrackPostHog()) {
		try {
			import("@/lib/services/posthog").then(({ trackContentCreation }) => {
				trackContentCreation(contentType, properties);
			});
		} catch (error) {
			console.error(
				"[PostHog Analytics] Error tracking content creation:",
				error,
			);
		}
	}
}

/**
 * Track social interactions beyond follows
 */
export function trackSocialInteraction(
	interaction: "like" | "share" | "comment" | "mention",
	properties?: Record<string, unknown>,
): void {
	// Only track in production
	if (process.env.NODE_ENV !== "production") {
		return;
	}

	const consentStore = useConsentStore.getState();

	if (consentStore.canTrackPostHog()) {
		try {
			import("@/lib/services/posthog").then(({ trackSocialInteraction }) => {
				trackSocialInteraction(interaction, properties);
			});

			// Also track as user activity for MAU/DAU metrics
			trackUserActivity("social_engagement", {
				interaction_type: interaction,
				...properties,
			});
		} catch (error) {
			console.error(
				"[PostHog Analytics] Error tracking social interaction:",
				error,
			);
		}
	}
}
